import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import Login from './components/auth/Login';
import { setAuthToken, hasSession, initializeAuth } from './services/api.ts';
import MedicalImageList from './components/images/MedicalImageList';
import StudyDatasetList from './components/datasets/StudyDatasetList.jsx';
import DatasetList from './components/datasets/DatasetList.jsx';
import MedicalDatasetCreator from './components/datasets/MedicalDatasetCreator.jsx';
import DatasetsPage from './components/datasets/DatasetsPage.jsx';
import LandingPage from './components/landing/landingPage';
import Dashboard from './components/home/dashboard.jsx';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import StudyDashboard from './components/studies/StudyDashboard.jsx';
import StudyList from './components/studies/StudyList.jsx';
import OpenDataset from './components/opendatasets/OpenDataset.jsx';
import DvViewer from './components/dv_viewer/DvViewer.jsx';
import ImageViewer from './components/dv_viewer/ImageViewer.jsx';
import DiscoveryViewerTest from './components/dv_viewer/DiscoveryViewerTest.jsx';
import DV2Viewer from './components/dv_viewer/DV2Viewer.jsx';
import CustomViewer from './components/dv_viewer/CustomViewer.jsx';
import EHRViewerPage from './components/dv_viewer/EHRViewerPage.jsx';
import SignalViewerPage from './components/dv_viewer/SignalViewerPage.jsx';
import DocumentViewerPage from './components/dv_viewer/DocumentViewerPage.jsx';
import ProjectList from './components/projects/ProjectList.jsx';
import ProjectDetail from './components/projects/ProjectDetail.jsx';
import NotificationList from './components/notifications/NotificationList.jsx';
import PatientCreator from './components/patients/PatientCreator.jsx';
import ModelList from './components/models/ModelList.jsx';
import NewModel from './components/models/NewModel.jsx';
import NewTool from './components/models/NewTool.jsx';
import RepositoryDetail from './components/registry/RepositoryDetail.jsx';
import OpenMedicalModels from './components/openmodels/OpenMedicalModels.jsx';
import OpenResearchStudies from './components/openstudies/OpenResearchStudies.jsx';
import ViewerCreationLab from './components/studies/ViewerCreationLab.jsx';
import Settings from './components/settings/Settings.jsx';
import ActivityLog from './components/activity/ActivityLog.jsx';


const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(hasSession());

  // On app load, restore the in-memory access token from the refresh token
  useEffect(() => {
    if (hasSession()) {
      initializeAuth().then((success) => {
        if (!success) setIsAuthenticated(false);
      });
    }
  }, []);

  return (
    <Router>
      <Routes>
        {/* Default route redirects to Login */}
        <Route path="/" element={<LandingPage />} />

        {/* Login Route */}
        <Route path="/login" element={<Login setIsAuthenticated={setIsAuthenticated} />} />

        {/* Register Route */}
        <Route path="/register" element={<Register setIsAuthenticated={setIsAuthenticated} />} />

        {/* Password reset routes (public) */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />

        {/* Protected Stories Route */}
        <Route
          path="/Images"
          element={isAuthenticated ? <MedicalImageList /> : <Navigate to="/login" />}
        />

        <Route
          path="/Datasets"
          element={isAuthenticated ? <DatasetsPage /> : <Navigate to="/login" />}
        />

        <Route
          path="/StudyDatasets/:StudyId"
          element={isAuthenticated ? <StudyDatasetList /> : <Navigate to="/login" />}
        />

        <Route
          path="/CreateDataset"
          element={isAuthenticated ? <MedicalDatasetCreator /> : <Navigate to="/login" />}
        />

        <Route
          path="/CreatePatient"
          element={isAuthenticated ? <PatientCreator /> : <Navigate to="/login" />}
        />

        <Route
          path="/Patients"
          element={isAuthenticated ? <PatientCreator /> : <Navigate to="/login" />}
        />

        <Route
          path="/Datasets/:DatasetId"
          element={isAuthenticated ? <DatasetList /> : <Navigate to="/login" />}
        />

        {/* Open Datasets - Public, no auth required */}
        <Route path="/OpenDatasets" element={<OpenDataset />} />

        {/* Redirect to home dashboard */}
        <Route
          path="/Dashboard"
          element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" />}
        />

        {/* Studies Routes */}
        <Route
          path="/Studies"
          element={isAuthenticated ? <StudyList mode="studies" /> : <Navigate to="/login" />}
        />

        {/* Viewers Route (dedicated viewers page) */}
        <Route
          path="/Viewers"
          element={isAuthenticated ? <StudyList mode="viewers" /> : <Navigate to="/login" />}
        />

        <Route
          path="/Study/:StudyId"
          element={isAuthenticated ? <StudyDashboard /> : <Navigate to="/login" />}
        />

        <Route
          path="/ViewerCreationLab"
          element={isAuthenticated ? <ViewerCreationLab /> : <Navigate to="/login" />}
        />

        <Route
          path="/DV_Viewer"
          element={isAuthenticated ? <DvViewer /> : <Navigate to="/login" />}
        />

        <Route
          path="/Image_Viewer"
          element={isAuthenticated ? <ImageViewer /> : <Navigate to="/login" />}
        />

        <Route
          path="/Discovery_Viewer_Test"
          element={isAuthenticated ? <DiscoveryViewerTest /> : <Navigate to="/login" />}
        />

        <Route
          path="/DV2"
          element={isAuthenticated ? <DV2Viewer /> : <Navigate to="/login" />}
        />

        <Route
          path="/Custom_Viewer"
          element={isAuthenticated ? <CustomViewer /> : <Navigate to="/login" />}
        />

        <Route
          path="/EHR_Viewer"
          element={isAuthenticated ? <EHRViewerPage /> : <Navigate to="/login" />}
        />

        <Route
          path="/Signal_Viewer"
          element={isAuthenticated ? <SignalViewerPage /> : <Navigate to="/login" />}
        />

        <Route
          path="/Document_Viewer"
          element={isAuthenticated ? <DocumentViewerPage /> : <Navigate to="/login" />}
        />

        {/* Projects Routes */}
        <Route
          path="/Projects"
          element={isAuthenticated ? <ProjectList /> : <Navigate to="/login" />}
        />

        <Route
          path="/Projects/:projectId"
          element={isAuthenticated ? <ProjectDetail /> : <Navigate to="/login" />}
        />

        {/* Notifications Route */}
        <Route
          path="/notifications"
          element={isAuthenticated ? <NotificationList /> : <Navigate to="/login" />}
        />

        {/* Models Routes */}
        <Route
          path="/Models"
          element={isAuthenticated ? <ModelList entity="model" /> : <Navigate to="/login" />}
        />

        <Route
          path="/Models/New"
          element={isAuthenticated ? <NewModel /> : <Navigate to="/login" />}
        />

        <Route
          path="/Models/NewTool"
          element={isAuthenticated ? <NewTool /> : <Navigate to="/login" />}
        />

        {/* Model Registry Routes - main registry lives inside Dev Tools */}
        <Route
          path="/Registry"
          element={isAuthenticated ? <Navigate to="/DevTools" /> : <Navigate to="/login" />}
        />

        <Route
          path="/Registry/:owner/:name"
          element={isAuthenticated ? <RepositoryDetail /> : <Navigate to="/login" />}
        />

        {/* Open Medical Models - Public, no auth required */}
        <Route path="/OpenModels" element={<OpenMedicalModels />} />

        {/* Open Research Studies - Public, no auth required */}
        <Route path="/OpenStudies" element={<OpenResearchStudies />} />

        {/* Activity Log Route (Admin only, component handles access control) */}
        <Route
          path="/ActivityLog"
          element={isAuthenticated ? <ActivityLog /> : <Navigate to="/login" />}
        />

        {/* Tools Route (dedicated processing tools page) */}
        <Route
          path="/Tools"
          element={isAuthenticated ? <ModelList entity="tool" /> : <Navigate to="/login" />}
        />

        {/* Profile Route (Settings — profile only) */}
        <Route
          path="/Profile"
          element={isAuthenticated ? <Settings mode="profile" /> : <Navigate to="/login" />}
        />

        {/* Dev Tools Route (Settings — CLI, Tokens, Registry) */}
        <Route
          path="/DevTools"
          element={isAuthenticated ? <Settings mode="devtools" /> : <Navigate to="/login" />}
        />

        {/* Catch-all route to redirect unknown paths to Login */}
        <Route path="*" element={<Navigate to="/" />} />

      </Routes>
    </Router>
  );
};

export default App;
